//External Libraries
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faChevronDown as farChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp as farChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faComment as farComment } from '@fortawesome/pro-regular-svg-icons/faComment';

//Hooks
import useComments from '../hooks/useComments';

//Utils
import { REPLIES_COLLAPSE_THRESHOLD } from '../utils';

//Components
import { CMButton } from '@chartmetric/chartmetric-design-system/components/CMButton';
import { CMFlex } from '@chartmetric/chartmetric-design-system/components/CMFlex';
import { CMText } from '@chartmetric/chartmetric-design-system/components/CMText';
import CommentThread from './CommentThread';
import CommentInput from './CommentInput';

//Types
import type { AnnouncementComment } from '../types';

interface AnnouncementCommentsProps {
  announcementId: number;
  initialComments?: AnnouncementComment[];
}

function AnnouncementComments({ announcementId, initialComments }: AnnouncementCommentsProps) {
  const { t } = useTranslation('common', { keyPrefix: 'announcementPanel' });
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);

  const {
    commentTree,
    commentCount,
    newComment,
    setNewComment,
    canSetDisplayName,
    displayName,
    setDisplayName,
    pendingImages,
    setPendingImages,
    isSubmitting,
    editingCommentId,
    replyingTo,
    handleAddComment,
    handleEditComment,
    handleDeleteComment,
    handleReply,
    cancelReply,
    isAuthenticated,
    requestSignIn,
    currentUserId,
  } = useComments({
    announcementId,
    isExpanded,
    initialComments,
  });

  return (
    <CMFlex vertical align="start">
      <CMButton
        text={commentCount > 0 ? `${t('comments.title')} (${commentCount})` : t('comments.title')}
        iconLeft={farComment}
        iconRight={isExpanded ? farChevronUp : farChevronDown}
        variant="secondaryText"
        size="sm"
        onClick={() => setIsExpanded((prev) => !prev)}
      />
      {isExpanded && (
        <CMFlex vertical mt="md" pl="md" gap="md" fullWidth>
          {(showAllComments || commentTree.length <= REPLIES_COLLAPSE_THRESHOLD
            ? commentTree
            : commentTree.slice(0, REPLIES_COLLAPSE_THRESHOLD)
          ).map((node) => (
            <CommentThread
              key={node.id}
              node={node}
              currentUserId={currentUserId}
              editingCommentId={editingCommentId}
              canSetDisplayName={canSetDisplayName}
              onEdit={handleEditComment}
              onDelete={(id) => { void handleDeleteComment(id); }}
              onReply={handleReply}
            />
          ))}

          {!showAllComments && commentTree.length > REPLIES_COLLAPSE_THRESHOLD && (
            <CMButton
              text={t('comments.showMore')}
              variant="secondaryText"
              size="xs"
              onClick={() => setShowAllComments(true)}
            />
          )}

          {commentTree.length === 0 && <CMText value={t('comments.empty')} variant="body" color="disabled" />}

          <CommentInput
            inputId={`comment-${announcementId}`}
            value={newComment}
            onChange={setNewComment}
            onSubmit={() => { void handleAddComment(); }}
            onFocus={() => { if (!isAuthenticated) requestSignIn(); }}
            isSubmitting={isSubmitting}
            placeholder={t('comments.placeholder')}
            replyingToName={replyingTo?.parentUserName ?? null}
            onCancelReply={cancelReply}
            canSetDisplayName={canSetDisplayName}
            displayName={displayName}
            onDisplayNameChange={setDisplayName}
            pendingImages={pendingImages}
            onImagesChange={setPendingImages}
          />
        </CMFlex>
      )}
    </CMFlex>
  );
}

export default AnnouncementComments;
